import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Filter } from 'lucide-react';
import { galleryImages, contactInfo } from '../mockData';

const Gallery = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const categories = [
    { id: 'all', label: 'Tout Voir' },
    { id: 'garage', label: 'Notre Garage' },
    { id: 'jantes', label: 'Jantes Premium' },
    { id: 'carrosserie', label: 'Carrosserie' },
    { id: 'peinture', label: 'Peinture' },
    { id: 'renovation', label: 'Rénovation' },
    { id: 'realisations', label: 'Réalisations' }
  ];

  const filteredImages = activeFilter === 'all'
    ? galleryImages
    : galleryImages.filter(img => img.category === activeFilter);

  return (
    <div className="gallery-page">
      {/* Hero Section */}
      <section className="gallery-hero">
        <div className="container">
          <div className="gallery-hero-content fade-in">
            <h1 className="page-title">
              Notre <span className="text-yellow">Galerie</span><br />
              de Réalisations
            </h1>
            <p className="page-subtitle">
              Découvrez notre savoir-faire à travers une sélection de nos meilleures réalisations. 
              Chaque projet reflète notre engagement envers l'excellence et la satisfaction client.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="filter-section section-padding">
        <div className="container">
          <div className="filter-header">
            <div className="filter-title">
              <Filter size={24} />
              <span>Filtrer par catégorie</span>
            </div>
          </div>
          <div className="filter-buttons">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`filter-btn ${activeFilter === category.id ? 'active' : ''}`}
                onClick={() => setActiveFilter(category.id)}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="gallery-grid-section">
        <div className="container">
          <div className="gallery-grid">
            {filteredImages.map((image, index) => (
              <div
                key={image.id}
                className="gallery-item fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="gallery-image-container">
                  <img src={image.image} alt={image.title} />
                  <div className="gallery-overlay">
                    <div className="gallery-info">
                      <h3 className="gallery-title">{image.title}</h3>
                      <span className="gallery-category">{categories.find(c => c.id === image.category)?.label}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {filteredImages.length === 0 && (
            <div className="no-results">
              <p>Aucune image trouvée pour cette catégorie.</p>
            </div>
          )}
        </div>
      </section>

      {/* Instagram Section */}
      <section className="instagram-section section-padding">
        <div className="container">
          <div className="instagram-content">
            <h2 className="section-title text-center mb-20">
              Suivez-nous sur <span className="text-yellow">Instagram</span>
            </h2>
            <p className="section-subtitle text-center mb-40">
              Découvrez nos dernières réalisations et actualités en temps réel
            </p>
            <div className="instagram-cta">
              <a
                href={contactInfo.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Voir sur Instagram
                <ArrowRight size={20} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="gallery-cta section-padding">
        <div className="container">
          <div className="gallery-cta-content">
            <h2 className="cta-title fade-in">
              Prêt pour Votre Projet ?
            </h2>
            <p className="cta-subtitle slide-in-left">
              Contactez-nous pour discuter de votre projet automobile
            </p>
            <div className="cta-buttons slide-in-right">
              <Link to="/contact" className="btn-primary">
                Demander un Devis
                <ArrowRight size={20} />
              </Link>
              <Link to="/services" className="btn-secondary">
                Voir Nos Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        /* Hero Section */
        .gallery-hero {
          background: linear-gradient(135deg, var(--bg-page) 0%, var(--bg-card) 100%);
          padding: 160px 40px 80px;
          border-bottom: 3px solid var(--brand-yellow);
        }

        .gallery-hero-content {
          max-width: 900px;
          margin: 0 auto;
          text-align: center;
        }

        .page-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 900;
          font-size: clamp(2.5rem, 6vw, 5rem);
          line-height: 1.1;
          color: var(--text-primary);
          margin-bottom: 24px;
        }

        .page-subtitle {
          font-size: clamp(1rem, 2vw, 1.3rem);
          line-height: 1.7;
          color: var(--text-secondary);
          max-width: 700px;
          margin: 0 auto;
        }

        /* Filter Section */
        .filter-section {
          background: var(--bg-card);
          padding: 40px;
        }

        .filter-header {
          margin-bottom: 24px;
        }

        .filter-title {
          display: flex;
          align-items: center;
          gap: 12px;
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          font-size: 1.3rem;
          color: var(--brand-yellow);
          margin-bottom: 20px;
        }

        .filter-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          justify-content: center;
        }

        .filter-btn {
          background: var(--bg-page);
          color: var(--text-primary);
          border: 2px solid var(--border-light);
          border-radius: var(--radius-pill);
          padding: 12px 24px;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .filter-btn:hover {
          border-color: var(--brand-yellow);
          color: var(--brand-yellow);
          transform: scale(1.05);
        }

        .filter-btn.active {
          background: var(--brand-yellow);
          color: var(--brand-black);
          border-color: var(--brand-yellow);
        }

        /* Gallery Grid */
        .gallery-grid-section {
          padding: 60px 40px;
          min-height: 400px;
        }

        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 24px;
        }

        .gallery-item {
          position: relative;
          overflow: hidden;
          border-radius: var(--radius-lg);
          cursor: pointer;
          transition: transform 0.3s ease;
        }

        .gallery-item:hover {
          transform: translateY(-8px);
        }

        .gallery-image-container {
          position: relative;
          width: 100%;
          height: 350px;
          overflow: hidden;
        }

        .gallery-image-container img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .gallery-item:hover img {
          transform: scale(1.15);
        }

        .gallery-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.9) 100%);
          display: flex;
          align-items: flex-end;
          padding: 24px;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .gallery-item:hover .gallery-overlay {
          opacity: 1;
        }

        .gallery-info {
          width: 100%;
        }

        .gallery-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          font-size: 1.2rem;
          color: var(--brand-yellow);
          margin-bottom: 8px;
        }

        .gallery-category {
          display: inline-block;
          background: var(--brand-yellow);
          color: var(--brand-black);
          padding: 4px 12px;
          border-radius: var(--radius-pill);
          font-size: 0.85rem;
          font-weight: 600;
        }

        .no-results {
          text-align: center;
          padding: 60px 20px;
          color: var(--text-secondary);
          font-size: 1.2rem;
        }

        /* Instagram Section */
        .instagram-section {
          background: var(--bg-card);
        }

        .instagram-content {
          max-width: 700px;
          margin: 0 auto;
        }

        .section-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 900;
          font-size: clamp(2rem, 4vw, 3rem);
          color: var(--text-primary);
        }

        .section-subtitle {
          font-size: 1.2rem;
          color: var(--text-secondary);
        }

        .instagram-cta {
          display: flex;
          justify-content: center;
        }

        /* CTA Section */
        .gallery-cta {
          background: linear-gradient(135deg, var(--brand-yellow) 0%, var(--brand-yellow-dark) 100%);
        }

        .gallery-cta-content {
          text-align: center;
          max-width: 800px;
          margin: 0 auto;
        }

        .cta-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 900;
          font-size: clamp(2rem, 4vw, 3.5rem);
          color: var(--brand-black);
          margin-bottom: 20px;
        }

        .cta-subtitle {
          font-size: 1.3rem;
          color: var(--brand-black);
          margin-bottom: 40px;
          opacity: 0.9;
        }

        .cta-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .gallery-cta .btn-primary {
          background: var(--brand-black);
          color: var(--brand-yellow);
        }

        .gallery-cta .btn-primary:hover {
          background: var(--brand-black-light);
        }

        .gallery-cta .btn-secondary {
          background: transparent;
          color: var(--brand-black);
          border-color: var(--brand-black);
        }

        .gallery-cta .btn-secondary:hover {
          background: var(--brand-black);
          color: var(--brand-yellow);
        }

        /* Responsive */
        @media (max-width: 767px) {
          .gallery-hero {
            padding: 120px 20px 60px;
          }

          .filter-section {
            padding: 30px 20px;
          }

          .filter-buttons {
            justify-content: flex-start;
          }

          .gallery-grid-section {
            padding: 40px 20px;
          }

          .gallery-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .gallery-image-container {
            height: 280px;
          }

          .cta-buttons {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default Gallery;
